/**
 * Hook de Gerenciamento de Informações do Arquivo
 * 
 * Gerencia dados relacionados ao arquivo sendo salvo:
 * - fileInfo: Nome e diretório do arquivo
 * - savedFilePath: Caminho completo salvo
 * - saveData: Dados de salvamento completos
 * 
 * Origem: linhas 39, 43-44, 61, 293-368, 1482-1548 do useSaveController.ts
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { readWithAliases } from '../../../lib/storage/keys';
import { Keys } from '../../../lib/storage/keys';
import { sendToTerminal } from '../../../lib/terminalLogger';
import { ensureCaminhoArquivo } from '../utils/saveDataTransformers';
import type { FileInfo } from '../types';

/**
 * Opções do hook useSaveFileInfo
 */
export interface UseSaveFileInfoOptions {
  /**
   * Se é usuário externo (revenda/cliente)
   */
  isExternalUser?: boolean;
}

/**
 * Retorno do hook useSaveFileInfo
 */
export interface UseSaveFileInfoReturn {
  // Estados
  /**
   * Informações do arquivo (nome e diretório)
   */
  fileInfo: FileInfo;
  
  /**
   * Dados de salvamento completos
   */
  saveData: any;
  
  /**
   * Caminho completo do arquivo salvo
   */
  savedFilePath: string;
  
  // Refs (para acesso direto)
  /**
   * Ref dos dados de salvamento
   */
  saveDataRef: React.MutableRefObject<any>;
  
  /**
   * Ref do caminho salvo
   */
  savedFilePathRef: React.MutableRefObject<string>;
  
  // Actions
  /**
   * Define informações do arquivo
   */
  setFileInfo: (info: FileInfo) => void;
  
  /**
   * Define dados de salvamento
   */
  setSaveData: (data: any) => void;
  
  /**
   * Define caminho salvo
   */
  setSavedFilePath: (path: string) => void;
  
  /**
   * Carrega informações do arquivo do storage
   */
  loadFileInfo: () => void;
  
  /**
   * Garante que caminho_arquivo está completo
   */
  ensureFilePath: (data: any) => any;
  
  /**
   * Atualiza fileInfo com base em saveData atual
   */
  updateFileInfoFromSaveData: () => void;
}

/**
 * Hook de gerenciamento de informações do arquivo
 * 
 * Centraliza toda a lógica relacionada aos dados do arquivo
 * sendo salvo, incluindo persistência e normalização.
 * 
 * Persistência:
 * - saveData é carregado do session storage (Keys.export.currentSaveData)
 * - savedFilePath é mantido em ref e pode vir do storage ou do CorelDRAW
 * 
 * @param options - Opções do hook
 * @returns Estados, refs e actions
 * 
 * @example
 * ```typescript
 * const fileInfo = useSaveFileInfo({ isExternalUser: false });
 * 
 * // Carregar do storage
 * fileInfo.loadFileInfo();
 * 
 * // Garantir caminho completo
 * const ensuredData = fileInfo.ensureFilePath(data);
 * 
 * // Atualizar após salvar no Corel
 * fileInfo.setSavedFilePath(caminhoSalvo);
 * fileInfo.updateFileInfoFromSaveData();
 * ```
 */
export function useSaveFileInfo(options: UseSaveFileInfoOptions = {}): UseSaveFileInfoReturn {
  const { isExternalUser = false } = options;

  // Estados
  const [fileInfo, setFileInfo] = useState<FileInfo>({ 
    fileName: '', 
    directory: '' 
  });
  const [saveData, setSaveDataState] = useState<any>(null);
  const [savedFilePath, setSavedFilePathState] = useState<string>('');

  // Refs para acesso direto
  const saveDataRef = useRef<any>(null);
  const savedFilePathRef = useRef<string>('');

  /**
   * Define dados de salvamento
   * 
   * Atualiza state e ref
   */
  const setSaveData = useCallback((data: any) => {
    saveDataRef.current = data;
    setSaveDataState(data);
  }, []);

  /**
   * Define caminho salvo
   * 
   * Atualiza state e ref
   */
  const setSavedFilePath = useCallback((path: string) => {
    savedFilePathRef.current = path;
    setSavedFilePathState(path);
  }, []);

  /**
   * Garante que caminho_arquivo está completo
   * 
   * Para fluxo TEMP:
   * - Tenta usar savedFilePathRef
   * - Senão, compõe de diretorios.interno + nome_arquivo
   * 
   * Para fluxo NORMAL:
   * - Tenta storage canônico
   * - Usa buildFilePath se necessário
   * 
   * @param data - Dados de salvamento
   * @returns Dados com caminho_arquivo garantido
   */
  const ensureFilePath = useCallback((data: any): any => {
    try {
      if (data?.fluxo?.tipo === 'temp') {
        if (!data.caminho_arquivo) {
          if (savedFilePathRef.current) {
            data.caminho_arquivo = savedFilePathRef.current;
          } else {
            const dirRaw = data?.diretorios?.interno || data?.diretorios?.externo || '';
            const nameRaw = data?.nome_arquivo || '';
            if (dirRaw && nameRaw) {
              const normalizedDir = dirRaw.endsWith('\\') ? dirRaw : `${dirRaw}\\`;
              data.caminho_arquivo = `${normalizedDir}${nameRaw}`;
            }
          }
        }
        if (typeof data.caminho_arquivo === 'string' && data.caminho_arquivo) {
          savedFilePathRef.current = data.caminho_arquivo;
          setSavedFilePathState(data.caminho_arquivo);
        }
        return data;
      }

      // Fluxo NORMAL: tentar usar caminho do storage canônico
      if (data && !data.caminho_arquivo) {
        try {
          const storedPath = readWithAliases<string>(
            Keys.document.lastResolvedFilePath,
            ['lastResolvedFilePath'],
            { area: 'session', persistIfAliased: true }
          );
          
          if (storedPath && typeof storedPath === 'string') {
            data.caminho_arquivo = storedPath;
            savedFilePathRef.current = storedPath;
            setSavedFilePathState(storedPath);
            return data;
          }
        } catch (err) {
          // Se storage falhar, continuar para próxima tentativa
        }
      }

      return data;
    } catch (err) {
      sendToTerminal('⚠️ [SAVE] Erro ao garantir caminho de arquivo', {
        error: err instanceof Error ? err.message : String(err)
      });
      return data;
    }
  }, []);

  /**
   * Carrega informações do arquivo do session storage
   * 
   * Lê de Keys.export.currentSaveData e atualiza todos os estados
   * 
   * OTIMIZAÇÃO: Usa refs para dados que não afetam UI, evitando re-renders desnecessários
   */
  const loadFileInfo = useCallback(() => {
    try {
      const data = readWithAliases<any>(
        Keys.export.currentSaveData,
        ['currentSaveData'],
        { area: 'session', persistIfAliased: true }
      );

      if (data) {
        // Garantir caminho completo
        const ensured = ensureFilePath({ ...data });
        
        // DEBUG: Log do que foi lido do storage
        sendToTerminal('🔍 [DEBUG:LOAD_FILE_INFO] Dados lidos do storage', {
          hasCaminhoArquivo: !!ensured.caminho_arquivo,
          caminhoArquivo: ensured.caminho_arquivo || null,
          diretorios: {
            interno: ensured.diretorios?.interno || null,
            externo: ensured.diretorios?.externo || null
          },
          orcId: ensured?.pedido?.orc_id,
          oitId: ensured?.pedido?.oit_id
        });
        
        // Atualizar ref diretamente (SEMPRE - usado pelo pipeline)
        saveDataRef.current = ensured;
        
        // Atualizar state com comparação inteligente
        setSaveDataState(prev => {
          // Se não existe prev, sempre atualizar
          if (!prev) {
            sendToTerminal('🔄 [DEBUG:LOAD_FILE_INFO] State atualizado (primeira vez)');
            return ensured;
          }
          
          // Comparar campos críticos para decidir se precisa re-render
          const idsChanged = prev?.pedido?.orc_id !== ensured?.pedido?.orc_id || 
                              prev?.pedido?.oit_id !== ensured?.pedido?.oit_id;
          const pathChanged = prev?.caminho_arquivo !== ensured?.caminho_arquivo;
          
          // Se algo importante mudou, atualizar
          if (idsChanged || pathChanged) {
            sendToTerminal('🔄 [DEBUG:LOAD_FILE_INFO] State atualizado', {
              idsChanged,
              pathChanged,
              prevPath: prev?.caminho_arquivo || null,
              newPath: ensured?.caminho_arquivo || null
            });
            return ensured;
          }
          
          // Caso contrário, manter prev para evitar re-render desnecessário
          sendToTerminal('⏭️ [DEBUG:LOAD_FILE_INFO] State mantido (sem mudanças críticas)');
          return prev;
        });

        // Extrair nome e diretório para UI
        const filePath = ensured.caminho_arquivo || 
          (isExternalUser ? ensured.diretorios?.externo : ensured.diretorios?.interno);
        const fileName = filePath?.split('\\').pop() || '';
        const directory = filePath?.substring(0, filePath.lastIndexOf('\\')) || '';

        if (filePath) {
          savedFilePathRef.current = filePath;
          setSavedFilePathState(filePath);
        }

        // Atualizar fileInfo apenas se mudou
        setFileInfo(prev => {
          if (prev.fileName === fileName && prev.directory === directory) {
            return prev;
          }
          return { fileName, directory };
        });

        // Log estruturado
        sendToTerminal('UI:LOAD_FILE_INFO', {
          fileName,
          directory,
          hasFilePath: !!filePath,
          fullPath: filePath || null,
          orcId: ensured?.pedido?.orc_id,
          oitId: ensured?.pedido?.oit_id
        });
      } else {
        sendToTerminal('⚠️ [SAVE] Nenhum dado encontrado no storage', {
          key: Keys.export.currentSaveData
        });
      }
    } catch (err) {
      sendToTerminal('❌ [SAVE] Erro ao carregar file info', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }, [isExternalUser, ensureFilePath]);

  /**
   * Atualiza fileInfo com base no saveData atual
   * 
   * Útil após salvar no Corel quando o caminho muda
   */
  const updateFileInfoFromSaveData = useCallback(() => {
    const data = saveDataRef.current;
    if (!data) return;

    const filePath = savedFilePathRef.current || 
      data.caminho_arquivo || 
      (isExternalUser ? data.diretorios?.externo : data.diretorios?.interno);

    if (filePath) {
      const fileName = filePath.split('\\').pop() || '';
      const directory = filePath.substring(0, filePath.lastIndexOf('\\')) || '';
      setFileInfo({ fileName, directory });

      sendToTerminal('🧾 [UI:UPDATE_FILE_INFO]', { fileName, directory });
    }
  }, [isExternalUser]);

  // Memoizar objeto retornado para evitar recriação a cada render
  return useMemo(() => ({
    // Estados
    fileInfo,
    saveData,
    savedFilePath,

    // Refs
    saveDataRef,
    savedFilePathRef,

    // Actions
    setFileInfo,
    setSaveData,
    setSavedFilePath,
    loadFileInfo,
    ensureFilePath,
    updateFileInfoFromSaveData,
  }), [
    fileInfo,
    saveData,
    savedFilePath,
    saveDataRef,
    savedFilePathRef,
    setFileInfo,
    setSaveData,
    setSavedFilePath,
    loadFileInfo,
    ensureFilePath,
    updateFileInfoFromSaveData,
  ]);
}

